import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AppMenuItem } from '@nx-ahs/shared/models';
import { AppMenuService } from '@nx-ahs/shared/services';

import { Subscription, firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class UserMenuPreferencesService implements OnDestroy {
	private _userFavoritesMenu: AppMenuItem[] = null;
	private _userMenuFavorites: string[] = null;
	private loading = false;
	private subscriptions: Subscription[] = [];

	public get userMenuFavorites() {
		return this._userMenuFavorites;
	}

	public userFavoritesMenuUpdated: EventEmitter<AppMenuItem[]> = new EventEmitter<AppMenuItem[]>();

	public get userFavoritesMenu() {
		return this._userFavoritesMenu;
	}

	constructor(
		private http: HttpClient,
		private appMenuService: AppMenuService
	) {
		this.subscriptions.push(
			this.appMenuService.appMenuUpdated.subscribe((newMenu) => {
				this.rebuildFavoritesMenu();
			})
		);
	}

	ngOnDestroy() {
		this.subscriptions.forEach((sub) => {
			sub.unsubscribe();
		});
	}

	public async addFavoriteMenuItem(menuItemId: string): Promise<any> {
		this.updateFavoriteMenuItem(menuItemId, true);
		const result = await this.http
			.post<any>(environment.apiUrl + `api/Main/AddFavoriteMenuItem?menuItemId=${menuItemId}`, null)
			.toPromise();
		return result;
	}

	public async removeFavoriteMenuItem(menuItemId: string): Promise<any> {
		this.updateFavoriteMenuItem(menuItemId, false);
		const result = await this.http
			.post<any>(environment.apiUrl + `api/Main/RemoveFavoriteMenuItem?menuItemId=${menuItemId}`, null)
			.toPromise();
		return result;
	}

	public async getUserFavoritedMenuItems(): Promise<AppMenuItem[]> {
		if (!this._userFavoritesMenu) {
			this.loading = true;
			this._userMenuFavorites = await firstValueFrom(
				this.http.get<string[]>(environment.apiUrl + `api/Main/GetUserFavoritedMenuItems`)
			);
			this.rebuildFavoritesMenu();
			this.loading = false;
		}

		return Promise.resolve(this.userFavoritesMenu);
	}

	public async markMainMenuConfigFavorites(menuConfig: AppMenuItem[]): Promise<AppMenuItem[]> {
		this.unFavoriteAllMenuItems(menuConfig);

		// Ensure userMenuFavorites is loaded
		await this.getUserFavoritedMenuItems();

		// Loop through provided menuConfig and look for favorites to mark
		menuConfig.forEach((menuItem) => {
			if (this.userMenuFavorites.includes(menuItem.menuItemId)) {
				menuItem.favorited = true;
			}

			if (menuItem.childMenu) {
				this.markMainMenuConfigFavorites(menuItem.childMenu);
			}
		});
		return menuConfig;
	}

	public rebuildFavoritesMenu() {
		if (this.appMenuService.appMenuConfig && this.userMenuFavorites) {
			this._userFavoritesMenu = undefined;
			const menu = [];
			this.userMenuFavorites.forEach((mi: string) => {
				let newMenu = undefined as AppMenuItem;
				newMenu = this.buildFavoritesMenusFromMenuConfig(
					JSON.parse(JSON.stringify(this.appMenuService.appMenuConfig)),
					mi,
					newMenu
				);
				if (newMenu) {
					newMenu.childMenu = null;
					menu.push(newMenu);
				}
			});

			this._userFavoritesMenu = menu;
			this.userFavoritesMenuUpdated.emit(this._userFavoritesMenu);
		}
	}

	private updateFavoriteMenuItem(menuItemId: string, favoriteStatus: boolean): void {
		if (favoriteStatus) this._userMenuFavorites.push(menuItemId);
		else {
			const index = this._userMenuFavorites.indexOf(menuItemId);
			if (index >= 0) this._userMenuFavorites.splice(index, 1);
		}

		this.rebuildFavoritesMenu();
	}

	private unFavoriteAllMenuItems(menuConfig: AppMenuItem[]) {
		menuConfig.forEach((menuItem, index) => {
			menuItem.favorited = false;

			if (menuItem.childMenu) {
				this.unFavoriteAllMenuItems(menuItem.childMenu);
			}
		});
	}

	private buildFavoritesMenusFromMenuConfig(
		menuConfig: AppMenuItem[],
		userFavoriteMenuId: string,
		newMenu: AppMenuItem
	): AppMenuItem {
		this.unFavoriteAllMenuItems(menuConfig);

		if (!newMenu) {
			for (let i = 0; i < menuConfig.length; i++) {
				const menuItem = menuConfig[i];

				if (userFavoriteMenuId == menuItem.menuItemId) {
					menuItem.favorited = true;
					newMenu = JSON.parse(JSON.stringify(menuItem));
					i = menuConfig.length;
				} else if (menuItem.childMenu) {
					newMenu = this.buildFavoritesMenusFromMenuConfig(menuItem.childMenu, userFavoriteMenuId, newMenu);
				}
			}
		}

		return newMenu;
	}
}
